import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faUsers,
  faBox,
} from "@fortawesome/pro-light-svg-icons";
import Select from "react-select";
import useApi from "../../../../hooks/useApi";
import Spinner from "../../../utils/Spinner";
import Button from "../../../buttons/Button";
import DecoratedInput from "../../../inputs/DecoratedInput";
import DatePickerButton from "../../../buttons/DatePickerButton";
import BookingDetailModal from "./BookingDetailModal";
import { getRestaurantBookings, cancelBooking } from "../../../../services/bookingService";
import { getRestaurantOrders } from "../../../../services/orderService";
import { getTableOverview } from "../../../../services/tableService";
import { toast } from "../../../../utils/demoToast";
import {
  getItemColorTheme,
} from "./BookingCalendarView";

const isCanceledBooking = (b) =>
  b.status === "canceled" || b.status === "declined";

const VISIBLE_ORDER_STATUS_IDS = [3, 4];
const ORDER_STATUS_MAP = {
  3: "confirmed",
  4: "confirmed",
};

const BookingTablesView = ({ hideCanceled = false, tKey = "dashboard.bookings." }) => {
  const { t } = useTranslation();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const controlledRestaurantId = useSelector(
    (state) => state.admin.controlledRestaurantId
  );
  const activeRestaurantId = controlledRestaurantId || restaurantId;

  // Default date range: today + 7 days
  const [dateRange, setDateRange] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);
    return { startDate: start, endDate: end };
  });

  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { request: fetchBookings, loading: bookingsLoading } =
    useApi(getRestaurantBookings);
  const { request: fetchOrders, loading: ordersLoading } =
    useApi(getRestaurantOrders);
  const { request: fetchTables, loading: tablesLoading } =
    useApi(getTableOverview);
  const { request: cancelBookingRequest } = useApi(cancelBooking);

  const fetchData = useCallback(async () => {
    if (!activeRestaurantId) return;

    try {
      const params = {
        from: dateRange.startDate.toISOString().split("T")[0],
        to: dateRange.endDate.toISOString().split("T")[0],
        limit: 500,
      };

      if (filterStatus) {
        params.status = filterStatus;
      }

      const from = dateRange.startDate.toISOString().split("T")[0];
      const to = dateRange.endDate.toISOString().split("T")[0];

      const [bookingsRes, ordersRes, tablesRes] = await Promise.all([
        fetchBookings(activeRestaurantId, params),
        fetchOrders(activeRestaurantId, from, to),
        fetchTables(activeRestaurantId),
      ]);

      setBookings(bookingsRes.data?.data || bookingsRes.data?.rows || []);
      setOrders(ordersRes.data || []);
      setTables(tablesRes.data?.rooms || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t(`${tKey}errors.fetchBookings`));
    }
  }, [
    activeRestaurantId,
    dateRange,
    filterStatus,
    fetchBookings,
    fetchOrders,
    fetchTables,
    t,
    tKey,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sort helper: canceled items always at the end, then by date
  const sortWithCanceledLast = (list) =>
    [...list].sort((a, b) => {
      const aCanceled = isCanceledBooking(a) ? 1 : 0;
      const bCanceled = isCanceledBooking(b) ? 1 : 0;
      if (aCanceled !== bCanceled) return aCanceled - bCanceled;
      return new Date(a.bookingDate) - new Date(b.bookingDate);
    });

  // Normalize orders into booking-like items
  const normalizedOrders = useMemo(() => {
    return orders
      .filter((o) => o.mealDate && VISIBLE_ORDER_STATUS_IDS.includes(o.orderStatusId))
      .map((order) => {
        const masterUser = order.masterUser;
        const displayName = masterUser
          ? `${masterUser.firstname || ""} ${masterUser.lastname || ""}`.trim()
          : order.orderCode || `#${order.orderId}`;
        const normalized = {
          ...order,
          type: "order",
          bookingId: `order-${order.orderId}`,
          bookingDate: order.mealDate || order.startAt,
          guestName: displayName,
          partySize: order.nbPerson || 1,
          status: ORDER_STATUS_MAP[order.orderStatusId] || "confirmed",
          guestEmail: masterUser?.email || null,
          guestPhone: masterUser?.phone || null,
          bookingCode: order.orderCode || `OF-${order.orderId}`,
          tableBookings: order.tableBookings || [],
        };
        normalized.colorTheme = getItemColorTheme(normalized);
        return normalized;
      });
  }, [orders]);

  // Client-side search + hide canceled filter (bookings + orders merged)
  const filteredBookings = useMemo(() => {
    const normalizedBookingItems = bookings.map((b) => ({
      ...b,
      type: "booking",
      colorTheme: getItemColorTheme({ ...b, type: "booking" }),
    }));
    let result = [...normalizedBookingItems, ...normalizedOrders];
    if (hideCanceled) {
      result = result.filter((b) => !isCanceledBooking(b));
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          b.guestName?.toLowerCase().includes(query) ||
          b.guestEmail?.toLowerCase().includes(query) ||
          b.guestPhone?.includes(query)
      );
    }
    return sortWithCanceledLast(result);
  }, [bookings, normalizedOrders, hideCanceled, searchQuery]);

  // Flatten tables from rooms
  const allTables = useMemo(
    () =>
      tables.flatMap((room) =>
        (room.tables || []).map((tbl) => ({
          ...tbl,
          roomName: room.name,
        }))
      ),
    [tables]
  );

  // Group bookings by table (canceled items at the end of each group)
  const bookingsByTable = useMemo(() => {
    return allTables.map((table) => ({
      table,
      bookings: sortWithCanceledLast(
        filteredBookings.filter((b) =>
          b.tableBookings?.some((tb) => tb.tableId === table.tableId)
        )
      ),
    }));
  }, [allTables, filteredBookings]);

  // Unassigned bookings (no tableBookings, canceled at end)
  const unassignedBookings = useMemo(() => {
    return sortWithCanceledLast(
      filteredBookings.filter(
        (b) => !b.tableBookings || b.tableBookings.length === 0
      )
    );
  }, [filteredBookings]);

  const handleDateRangeChange = (selection) => {
    setDateRange({
      startDate: selection.startDate,
      endDate: selection.endDate,
    });
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(t(`${tKey}confirm.cancelBooking`))) return;
    try {
      await cancelBookingRequest(bookingId);
      toast.success(t(`${tKey}success.bookingCanceled`));
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      console.error("Error canceling booking:", error);
      toast.error(t(`${tKey}errors.cancelBooking`));
    }
  };

  // Build status filter options
  const statusOptions = useMemo(
    () => [
      { value: "pending", label: t(`${tKey}status.pending`) },
      { value: "confirmed", label: t(`${tKey}status.confirmed`) },
      { value: "declined", label: t(`${tKey}status.declined`) },
      { value: "canceled", label: t(`${tKey}status.canceled`) },
    ],
    [t, tKey]
  );

  const selectedStatusOption =
    statusOptions.find((o) => o.value === filterStatus) || null;

  const hasFilters = filterStatus || searchQuery;

  const resetFilters = () => {
    setFilterStatus(null);
    setSearchQuery("");
  };

  const loading = bookingsLoading || ordersLoading || tablesLoading;

  if (loading && bookings.length === 0) {
    return (
      <div className="flex justify-center align-center p-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bookings-tables">
      {/* Filters */}
      <div className="bookings-tables__filters search-module">
        <DatePickerButton
          variant="tertiary"
          onDateRangeChange={handleDateRangeChange}
          startAt={dateRange.startDate}
          endAt={dateRange.endDate}
          maxDate={new Date(new Date().getFullYear() + 1, 11, 31)}
        />
        <DecoratedInput
          className="bg-pastel-light dashboard-search-bar"
          input={{
            id: "searchBooking",
            class: "p4",
            value: "",
            placeholder: t(`${tKey}tables.searchPlaceholder`),
          }}
          hasResearchButton
          onResearchButtonClick={(query) => setSearchQuery(query)}
        />
        <Select
          className="react-custom-select dashboard-select"
          classNamePrefix="react-select"
          placeholder={t(`${tKey}calendar.allStatuses`)}
          options={statusOptions}
          value={selectedStatusOption}
          isClearable
          isSearchable={false}
          onChange={(option) => setFilterStatus(option?.value || null)}
        />
        {hasFilters && (
          <Button
            variant="white"
            size="xs"
            onClick={resetFilters}
          >
            {t(`${tKey}tables.reset`)}
          </Button>
        )}
      </div>

      {/* Content */}
      <div className="bookings-tables__content">
        {/* Unassigned Bookings */}
        {unassignedBookings.length > 0 && (
          <section>
            <h4 className="bookings-tables__section-title">
              {t(`${tKey}tables.unassigned`)} ({unassignedBookings.length})
            </h4>
            <div className="bookings-tables__list">
              {unassignedBookings.map((booking) => (
                <BookingRow
                  key={booking.bookingId}
                  booking={booking}
                  onViewDetails={() => setSelectedBooking(booking)}
                  tKey={tKey}
                />
              ))}
            </div>
          </section>
        )}

        {/* Bookings grouped by table */}
        {bookingsByTable.map(({ table, bookings: tableBookings }) => {
          if (tableBookings.length === 0) return null;

          return (
            <section key={table.tableId}>
              <div className="flex justify-between align-center mb-2">
                <h4 className="bookings-tables__section-title m-0">
                  {table.name}
                  {table.roomName ? ` - ${table.roomName}` : ""}
                </h4>
                <div className="bookings-tables__section-meta">
                  <FontAwesomeIcon icon={faUsers} />
                  <span>{table.numberOfPlaces} {t(`${tKey}detail.guests`)}</span>
                  <span>&bull;</span>
                  <span>
                    {tableBookings.length}{" "}
                    {t(`${tKey}calendar.bookingsShort`)}
                  </span>
                </div>
              </div>

              <div className="bookings-tables__list">
                {tableBookings.map((booking) => (
                  <BookingRow
                    key={booking.bookingId}
                    booking={booking}
                    onViewDetails={() => setSelectedBooking(booking)}
                    tKey={tKey}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* Empty state */}
        {filteredBookings.length === 0 && (
          <div className="bookings-tables__list">
            <div className="bookings-tables__empty">
              {t(`${tKey}tables.noBookings`)}
            </div>
          </div>
        )}
      </div>

      <BookingDetailModal
        item={selectedBooking}
        tables={tables}
        isOpen={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onCancel={handleCancelBooking}
      />
    </div>
  );
};

// --- Booking Row Sub-Component ---
const BookingRow = ({ booking, onViewDetails, tKey }) => {
  const { t } = useTranslation();
  const isPreorder =
    booking.source === "booking" && booking.preorderItems?.length > 0;
  const bookingDate = new Date(booking.bookingDate);

  const getStatusKey = (status) => {
    const map = {
      pending: "pending",
      confirmed: "confirmed",
      declined: "declined",
      canceled: "canceled",
      no_show: "noShow",
      completed: "completed",
    };
    return map[status] || status;
  };

  const canceled = isCanceledBooking(booking);
  const isOrder = booking.type === "order";
  const themeClass = booking.colorTheme ? `booking-row--${booking.colorTheme}` : "";

  return (
    <div
      className={`booking-row ${themeClass} ${isPreorder ? "booking-row--preorder" : ""} ${canceled ? "booking-row--canceled" : ""}`}
      onClick={onViewDetails}
    >
      <div className="booking-row__top">
        <div className="booking-row__info">
          <div className="booking-row__name-row">
            <span className="booking-row__name">{booking.guestName}</span>
            {isOrder && (
              <span className="booking-row__preorder-badge">
                <FontAwesomeIcon icon={faBox} />
                <span>{t(`${tKey}detail.order`)}</span>
              </span>
            )}
            {isPreorder && !isOrder && (
              <span className="booking-row__preorder-badge">
                <FontAwesomeIcon icon={faBox} />
                <span>{t(`${tKey}detail.preorder`)}</span>
              </span>
            )}
            <span
              className={`booking-status booking-status--${booking.status}`}
            >
              {t(`${tKey}status.${getStatusKey(booking.status)}`)}
            </span>
          </div>

          <div className="booking-row__details">
            <div className="booking-row__detail">
              <FontAwesomeIcon icon={faCalendar} className="fa-icon" />
              <span>
                {bookingDate.toLocaleDateString("fr-FR", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </span>
            </div>
            <div className="booking-row__detail">
              <FontAwesomeIcon icon={faClock} className="fa-icon" />
              <span>
                {bookingDate.toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <div className="booking-row__detail">
              <FontAwesomeIcon icon={faUsers} className="fa-icon" />
              <span>
                {booking.partySize} {t(`${tKey}detail.guests`)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingTablesView;
